import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import http from "../api/apiClient";
import { FaClock, FaEnvelope, FaExclamationTriangle, FaSyncAlt, FaTools } from "react-icons/fa";
import Modal from "../components/Modal";
import { useCan } from "../authz/useCan";
import {
  LdsOverviewCard,
  LdsOverviewGrid,
  LdsOverviewHero,
  LdsOverviewPage,
} from "../components/lds/LdsOverview";

type ModalState = {
  show: boolean;
  title: string;
  message: string;
  type: "info" | "success" | "error";
};

type MailchimpTileState = {
  loading: boolean;
  ok: boolean | null;
  neverSynced: boolean;
  error: boolean;
};

const AdminMaintenance = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canExpire = useCan("outing.info", "manage");
  const canMailchimpStatus = useCan("newsletter.recipients", "status");
  const canMailchimpExport = useCan("newsletter.recipients", "export");
  const canMailchimpSync = useCan("newsletter.recipients", "sync");
  const canSeeMailchimp = canMailchimpStatus || (canMailchimpExport && canMailchimpSync);
  const canManageEmailValidationDomains = useCan("email.validation", "manage");
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState<ModalState>({ show: false, title: "", message: "", type: "info" });
  const [mailchimpTileState, setMailchimpTileState] = useState<MailchimpTileState>({
    loading: false,
    ok: null,
    neverSynced: false,
    error: false,
  });

  const mailchimpBadge = useMemo(() => {
    if (!canSeeMailchimp) return null;
    if (mailchimpTileState.loading) {
      return { variant: "loading", label: t("common.loading", { defaultValue: "Bezig..." }) };
    }
    if (mailchimpTileState.neverSynced) {
      return { variant: "neutral", label: t("admin.maintenance.mailchimp.never_synced", { defaultValue: "Nog nooit" }) };
    }
    if (mailchimpTileState.error) {
      return { variant: "warn", label: t("common.unknown", { defaultValue: "Onbekend" }) };
    }
    if (mailchimpTileState.ok === true) {
      return { variant: "ok", label: t("common.ok", { defaultValue: "OK" }) };
    }
    if (mailchimpTileState.ok === false) {
      return { variant: "bad", label: t("common.error", { defaultValue: "Fout" }) };
    }
    return null;
  }, [canSeeMailchimp, mailchimpTileState, t]);

  useEffect(() => {
    if (!canMailchimpStatus) return;
    let cancelled = false;
    const load = async () => {
      setMailchimpTileState((s) => ({ ...s, loading: true, error: false }));
      try {
        const res = await http.get("/admin/newsletter/recipients/mailchimp/status", {
          validateStatus: (code) => (code >= 200 && code < 300) || code === 204,
        });
        if (cancelled) return;
        if (res.status === 204) {
          setMailchimpTileState({ loading: false, ok: null, neverSynced: true, error: false });
          return;
        }
        const ok = Boolean(res?.data?.ok);
        setMailchimpTileState({ loading: false, ok, neverSynced: false, error: false });
      } catch {
        if (cancelled) return;
        setMailchimpTileState({ loading: false, ok: null, neverSynced: false, error: true });
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [canMailchimpStatus]);

  const handleExpireNow = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await http.post("/outing-info/expire-now");
      setModal({
        show: true,
        type: "success",
        title: t("admin.maintenance.expire_success_title", { defaultValue: "Gelukt" }),
        message: t("admin.maintenance.expire_success", { defaultValue: "Uitjes zijn vervallen gezet." }),
      });
    } catch {
      setModal({
        show: true,
        type: "error",
        title: t("common.error", { defaultValue: "Fout" }),
        message: t("admin.maintenance.expire_error", {
          defaultValue: "Kon de uitjes niet vervallen zetten.",
        }),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <LdsOverviewPage>
      <LdsOverviewHero
        icon={FaTools}
        eyebrow={t("navigation.management", { defaultValue: "Beheer" })}
        title={t("admin.maintenance.title", { defaultValue: "Maintenance" })}
        description={t("admin.maintenance.overview_description", {
          defaultValue: "Onderhoudsacties voor uitjes, Mailchimp en email-validatie.",
        })}
      />

      <LdsOverviewGrid>
        {canExpire && (
          <article className="lds-overview-card-enter group relative flex min-h-[260px] flex-col overflow-hidden rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-indigo-200 hover:shadow-[0_24px_60px_rgba(79,70,229,0.16)] dark:border-white/10 dark:bg-slate-900/80 dark:hover:border-indigo-400/30">
            <div className="absolute inset-x-0 top-0 h-1 origin-left scale-x-0 bg-gradient-to-r from-violet-500 via-indigo-500 to-sky-400 transition-transform duration-500 group-hover:scale-x-100" />
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-100 to-sky-50 text-2xl text-violet-700 shadow-sm dark:from-violet-800/40 dark:to-sky-900/30 dark:text-violet-200">
              <FaClock aria-hidden="true" />
            </div>
            <div className="mt-5">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-indigo-700 dark:text-indigo-300">
                {t("admin.maintenance.actions_category", { defaultValue: "Onderhoud" })}
              </p>
              <h2 className="mt-2 text-2xl font-bold text-slate-950 dark:text-white">
                {t("admin.maintenance.expire_title", { defaultValue: "Uitjes direct laten vervallen" })}
              </h2>
            </div>
            <p className="mt-4 flex-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
              {t("admin.maintenance.expire_desc", {
                defaultValue: "POST /api/v1/outing-info/expire-now (alle actieve uitjes worden direct vervallen).",
              })}
            </p>
            <button
              type="button"
              onClick={handleExpireNow}
              disabled={busy}
              className="mt-6 inline-flex w-fit items-center justify-center rounded-full border border-indigo-200 bg-indigo-50 px-5 py-2.5 text-sm font-bold text-indigo-700 shadow-sm transition hover:border-indigo-300 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:cursor-not-allowed disabled:opacity-60 dark:border-indigo-400/30 dark:bg-indigo-400/10 dark:text-indigo-200 dark:hover:bg-indigo-400/20"
            >
              {busy
                ? t("common.loading", { defaultValue: "Bezig..." })
                : t("admin.maintenance.expire_cta", { defaultValue: "Start expire" })}
            </button>
          </article>
        )}

        {canSeeMailchimp && (
          <button
            type="button"
            onClick={() => navigate("/integrations/mailchimp")}
            className="lds-overview-card-enter group relative flex min-h-[260px] flex-col overflow-hidden rounded-[24px] border border-slate-200 bg-white p-6 text-left shadow-sm transition duration-300 hover:-translate-y-1 hover:border-indigo-200 hover:shadow-[0_24px_60px_rgba(79,70,229,0.16)] dark:border-white/10 dark:bg-slate-900/80 dark:hover:border-indigo-400/30"
            style={{ animationDelay: "120ms" }}
          >
            <div className="absolute inset-x-0 top-0 h-1 origin-left scale-x-0 bg-gradient-to-r from-violet-500 via-indigo-500 to-sky-400 transition-transform duration-500 group-hover:scale-x-100" />
            <FaSyncAlt
              aria-hidden="true"
              className="pointer-events-none absolute -bottom-8 -right-7 text-[9rem] text-indigo-200/55 dark:text-indigo-400/10"
            />
            <div className="relative z-10 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-100 to-sky-50 text-2xl text-violet-700 shadow-sm dark:from-violet-800/40 dark:to-sky-900/30 dark:text-violet-200">
              <FaSyncAlt aria-hidden="true" />
            </div>
            <div className="relative z-10 mt-5 flex-1">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-indigo-700 dark:text-indigo-300">
                {t("admin.maintenance.integrations_category", { defaultValue: "Integraties" })}
              </p>
              <h2 className="mt-2 text-2xl font-bold text-slate-950 dark:text-white">
                {t("admin.maintenance.mailchimp.title", { defaultValue: "Mailchimp sync" })}
              </h2>
              <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300">
                {t("admin.maintenance.mailchimp.description", { defaultValue: "Sync starten en status uitlezen." })}
              </p>
            </div>
            <div className="relative z-10 mt-6 flex flex-wrap items-center justify-between gap-3">
              <span className="text-sm font-bold text-indigo-700 dark:text-indigo-200">
                {t("common.open", { defaultValue: "Open" })}
              </span>
              {mailchimpBadge && (
                <span
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold shadow-sm ${
                    mailchimpBadge.variant === "ok"
                      ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-900/25 dark:text-emerald-100"
                      : mailchimpBadge.variant === "bad"
                        ? "border-red-200 bg-red-50 text-red-800 dark:border-red-500/40 dark:bg-red-900/25 dark:text-red-100"
                        : mailchimpBadge.variant === "warn" || mailchimpBadge.variant === "loading"
                          ? "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/40 dark:bg-amber-900/25 dark:text-amber-100"
                          : "border-gray-200 bg-gray-50 text-gray-800 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-100"
                  }`}
                >
                  {mailchimpBadge.variant === "ok" ? (
                    <span className="inline-flex animate-spin" style={{ animationDuration: "2.6s" }}>
                      <FaSyncAlt />
                    </span>
                  ) : mailchimpBadge.variant === "bad" || mailchimpBadge.variant === "warn" ? (
                    <FaExclamationTriangle />
                  ) : mailchimpBadge.variant === "loading" ? (
                    <span className="inline-flex animate-spin" style={{ animationDuration: "1.2s" }}>
                      <FaSyncAlt />
                    </span>
                  ) : null}
                  {mailchimpBadge.label}
                </span>
              )}
            </div>
          </button>
        )}

        {canManageEmailValidationDomains && (
          <LdsOverviewCard
            icon={FaEnvelope}
            eyebrow={t("admin.maintenance.validation_category", { defaultValue: "Validatie" })}
            title={t("admin.email_validation.title", { defaultValue: "Email validation domains" })}
            description={t("admin.email_validation.desc", {
                defaultValue: "Beheer domeinregels voor email-validatie (typo’s, suggesties).",
              })}
            actionLabel={t("common.open", { defaultValue: "Open" })}
            onClick={() => navigate("/maintenance/email-validation/domains")}
            animationIndex={2}
          />
        )}

      </LdsOverviewGrid>

      <Modal
        show={modal.show}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        onClose={() => setModal({ show: false, title: "", message: "", type: "info" })}
      />
    </LdsOverviewPage>
  );
};

export default AdminMaintenance;
